import * as React from 'react';
import { Check, Trash2 } from 'lucide-react';
import { Select, type SelectOption, type SelectOptionGroup } from './select';
import { cn } from '../../utils';

export interface FamilyAllowanceFundOption {
  year: number;
  number: string;
  name: string;
  aliases: string[];
  active: boolean;
  cantons: Array<{ canton: string; active: boolean }>;
  avsAffiliations: Array<{
    canton: string;
    number: string | null;
    name: string | null;
  }>;
}

export interface FamilyAllowanceFundSelectLabels {
  placeholder: string;
  search: string;
  noResults: string;
  recommended: string;
  other: string;
  inactive: string;
  legacy: string;
  legacyWarning: string;
  remove: string;
}

const DEFAULT_LABELS: FamilyAllowanceFundSelectLabels = {
  placeholder: 'Select a family allowance fund',
  search: 'Search by name, number, canton or AVS fund…',
  noResults: 'No family allowance fund found.',
  recommended: 'Active in the selected canton',
  other: 'Other active funds',
  inactive: 'Inactive funds',
  legacy: 'Saved value',
  legacyWarning: 'Unrecognized value — please verify it',
  remove: 'Remove selection',
};

export interface FamilyAllowanceFundSelectProps {
  funds: FamilyAllowanceFundOption[];
  value?: string;
  onChange?: (number: string) => void;
  onClear?: () => void;
  preferredCanton?: string;
  legacyFund?: { number?: string; name: string };
  labels?: Partial<FamilyAllowanceFundSelectLabels>;
  support: React.ReactNode;
  referenceYearHint?: string;
  disabled?: boolean;
  error?: boolean;
  id?: string;
  className?: string;
  disablePortal?: boolean;
}

export function normalizeFamilyAllowanceFundSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export function matchesFamilyAllowanceFund(
  fund: FamilyAllowanceFundOption,
  query: string,
): boolean {
  const haystack = normalizeFamilyAllowanceFundSearch(
    [
      fund.number,
      fund.name,
      ...fund.aliases,
      ...fund.cantons.map((entry) => entry.canton),
      ...fund.avsAffiliations.flatMap((entry) => [
        entry.canton,
        entry.number ?? '',
        entry.name ?? '',
      ]),
    ].join(' '),
  );
  return normalizeFamilyAllowanceFundSearch(query)
    .split(' ')
    .filter(Boolean)
    .every((word) => haystack.includes(word));
}

export function groupFamilyAllowanceFunds(
  funds: FamilyAllowanceFundOption[],
  preferredCanton: string | undefined,
  labels: FamilyAllowanceFundSelectLabels,
): SelectOptionGroup[] {
  const option = (fund: FamilyAllowanceFundOption): SelectOption => ({
    value: fund.number,
    label: `${fund.name} (${fund.number})`,
    disabled: !fund.active,
  });
  const preferred = funds.filter(
    (fund) =>
      fund.active &&
      preferredCanton &&
      fund.cantons.some(
        (entry) => entry.canton === preferredCanton && entry.active,
      ),
  );
  const preferredNumbers = new Set(preferred.map((fund) => fund.number));
  const other = funds.filter(
    (fund) => fund.active && !preferredNumbers.has(fund.number),
  );
  const inactive = funds.filter((fund) => !fund.active);
  const sort = (a: FamilyAllowanceFundOption, b: FamilyAllowanceFundOption) =>
    a.name.localeCompare(b.name) || a.number.localeCompare(b.number);
  return [
    preferred.length
      ? { label: labels.recommended, options: preferred.sort(sort).map(option) }
      : null,
    { label: labels.other, options: other.sort(sort).map(option) },
    inactive.length
      ? { label: labels.inactive, options: inactive.sort(sort).map(option) }
      : null,
  ].filter(
    (group): group is SelectOptionGroup =>
      group !== null && group.options.length > 0,
  );
}

const FamilyAllowanceFundSelect = React.forwardRef<
  HTMLButtonElement,
  FamilyAllowanceFundSelectProps
>(function FamilyAllowanceFundSelect(
  {
    funds,
    value,
    onChange,
    onClear,
    preferredCanton,
    legacyFund,
    labels: overrides,
    support,
    referenceYearHint,
    disabled,
    error,
    id,
    className,
    disablePortal,
  },
  ref,
) {
  const labels = React.useMemo(
    () => ({ ...DEFAULT_LABELS, ...overrides }),
    [overrides],
  );
  const byNumber = React.useMemo(
    () => new Map(funds.map((fund) => [fund.number, fund])),
    [funds],
  );
  const groups = React.useMemo(() => {
    const result = groupFamilyAllowanceFunds(funds, preferredCanton, labels);
    if (legacyFund) {
      result.unshift({
        label: labels.legacy,
        options: [
          {
            value: legacyFund.number || '__legacy_fak__',
            label: `${legacyFund.name} — ${labels.legacyWarning}`,
            disabled: true,
          },
        ],
      });
    }
    return result;
  }, [funds, preferredCanton, labels, legacyFund]);

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-center gap-2">
        <Select
          ref={ref}
          id={id}
          className="min-w-0 flex-1"
          options={groups}
          value={legacyFund ? legacyFund.number || '__legacy_fak__' : value}
          onChange={onChange}
          placeholder={labels.placeholder}
          disabled={disabled}
          error={error}
          searchable
          searchPlaceholder={labels.search}
          noResultsText={labels.noResults}
          filterOption={(selectOption, query) => {
            const fund = byNumber.get(selectOption.value);
            return fund
              ? matchesFamilyAllowanceFund(fund, query)
              : normalizeFamilyAllowanceFundSearch(selectOption.label).includes(
                  normalizeFamilyAllowanceFundSearch(query),
                );
          }}
          disablePortal={disablePortal}
          renderOption={(selectOption, selected) => {
            const fund = byNumber.get(selectOption.value);
            if (!fund)
              return (
                <span className="text-amber-700">{selectOption.label}</span>
              );
            const activeCantons = fund.cantons
              .filter((entry) => entry.active)
              .map((entry) => entry.canton)
              .join(', ');
            const affiliations = [
              ...new Set(
                fund.avsAffiliations
                  .map((entry) =>
                    [entry.number, entry.name].filter(Boolean).join(' '),
                  )
                  .filter(Boolean),
              ),
            ].join(' · ');
            return (
              <>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">
                    {fund.name}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {[fund.number, activeCantons, affiliations]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                {selected && (
                  <Check className="size-4 shrink-0 text-riso-teal" />
                )}
              </>
            );
          }}
        />
        {(value || legacyFund) && onClear && (
          <button
            type="button"
            aria-label={labels.remove}
            title={labels.remove}
            onClick={onClear}
            className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Trash2 className="size-4" />
          </button>
        )}
      </div>
      {legacyFund && (
        <p className="text-xs font-medium text-amber-700">
          {labels.legacyWarning}
        </p>
      )}
      {referenceYearHint && (
        <p className="text-xs text-muted-foreground">{referenceYearHint}</p>
      )}
      <div className="text-xs text-muted-foreground">{support}</div>
    </div>
  );
});

export { FamilyAllowanceFundSelect };
